import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMunicipalities, useClients } from "@/hooks";
import { api } from "@/lib/api";
import type {
  ProjectCreatePayload,
  MunicipalityCreatePayload,
  AreaPilotoCreatePayload,
  MunicipalityApi,
  ProjectCreatedApi,
} from "@/lib/api";

const PROJECT_TYPES = [
  { value: "IMPLANTACAO_CTA_MUNICIPAL", label: "Implantação de CTA Municipal" },
  { value: "INVENTARIO_ARBOREO_URBANO", label: "Inventário Arbóreo Urbano" },
  { value: "INVENTARIO_PRACA", label: "Inventário de Praça" },
  { value: "INVENTARIO_PARQUE", label: "Inventário de Parque" },
  { value: "INVENTARIO_UC", label: "Inventário de Unidade de Conservação" },
  { value: "MONITORAMENTO_RISCO", label: "Monitoramento de Risco" },
  { value: "COMPENSACAO_AMBIENTAL", label: "Compensação Ambiental" },
  { value: "CARBONO_URBANO", label: "Carbono Urbano" },
  { value: "LEVANTAMENTO_3D", label: "Levantamento 3D" },
];

const UFS = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
  "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO",
];

const STEPS = [
  { label: "Projeto", icon: "ri-folder-chart-line" },
  { label: "Município", icon: "ri-map-pin-line" },
  { label: "Área de Estudo", icon: "ri-map-2-line" },
  { label: "Resumo", icon: "ri-check-double-line" },
];

interface ProjectForm {
  name: string;
  project_type: string;
  description: string;
  client_id: number | null;
  start_date: string;
  expected_end_date: string;
}

interface MunicipalityForm {
  mode: "existing" | "new";
  existing_id: number | null;
  name: string;
  uf: string;
  ibge_code: string;
}

interface AreaEstudoForm {
  skip: boolean;
  name: string;
  code: string;
  neighborhood: string;
  expected_block_count: string;
  description: string;
}

export default function NovoProjeto() {
  const navigate = useNavigate();
  const { municipalities, loading: loadingMunicipalities } = useMunicipalities();
  const { clients, loading: loadingClients } = useClients();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [projectForm, setProjectForm] = useState<ProjectForm>({
    name: "",
    project_type: "",
    description: "",
    client_id: null,
    start_date: "",
    expected_end_date: "",
  });

  const [municipalityForm, setMunicipalityForm] = useState<MunicipalityForm>({
    mode: "existing",
    existing_id: null,
    name: "",
    uf: "GO",
    ibge_code: "",
  });

  const [areaForm, setAreaForm] = useState<AreaEstudoForm>({
    skip: false,
    name: "",
    code: "",
    neighborhood: "",
    expected_block_count: "",
    description: "",
  });

  const [createdProject, setCreatedProject] = useState<ProjectCreatedApi | null>(null);
  const [resolvedMunicipalityId, setResolvedMunicipalityId] = useState<number | null>(null);

  function canAdvance(): boolean {
    if (step === 0) {
      return projectForm.name.trim().length > 0 && projectForm.project_type.length > 0;
    }
    if (step === 1) {
      if (municipalityForm.mode === "existing") return municipalityForm.existing_id !== null;
      return municipalityForm.name.trim().length > 0 && municipalityForm.uf.length === 2;
    }
    if (step === 2) {
      if (areaForm.skip) return true;
      return areaForm.name.trim().length > 0 && areaForm.code.trim().length > 0;
    }
    return true;
  }

  function selectedMunicipality(): MunicipalityApi | undefined {
    if (municipalityForm.mode === "existing" && municipalityForm.existing_id) {
      return municipalities.find((m) => m.id === municipalityForm.existing_id);
    }
    return undefined;
  }

  function projectTypeLabel(): string {
    return PROJECT_TYPES.find((t) => t.value === projectForm.project_type)?.label ?? projectForm.project_type;
  }

  function clientName(): string {
    if (!projectForm.client_id) return "Nenhum";
    return clients.find((c) => c.id === projectForm.client_id)?.name ?? `#${projectForm.client_id}`;
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);

    try {
      let municipalityId: number | null = null;

      if (municipalityForm.mode === "existing") {
        municipalityId = municipalityForm.existing_id;
      } else {
        const payload: MunicipalityCreatePayload = {
          name: municipalityForm.name.trim(),
          uf: municipalityForm.uf.toUpperCase(),
          ibge_code: municipalityForm.ibge_code.trim() || null,
        };
        const created = await api.createMunicipality(payload);
        municipalityId = created.id;
      }
      setResolvedMunicipalityId(municipalityId);

      const projectPayload: ProjectCreatePayload = {
        name: projectForm.name.trim(),
        project_type: projectForm.project_type,
        description: projectForm.description.trim() || null,
        client_id: projectForm.client_id,
        municipality_id: municipalityId,
        start_date: projectForm.start_date || null,
        expected_end_date: projectForm.expected_end_date || null,
      };
      const project = await api.createProject(projectPayload);
      setCreatedProject(project);

      if (!areaForm.skip && areaForm.name.trim()) {
        const areaPayload: AreaPilotoCreatePayload = {
          name: areaForm.name.trim(),
          code: areaForm.code.trim(),
          project_id: project.id,
          municipality_id: municipalityId,
          neighborhood: areaForm.neighborhood.trim() || null,
          expected_block_count: areaForm.expected_block_count ? parseInt(areaForm.expected_block_count, 10) : null,
          description: areaForm.description.trim() || null,
        };
        await api.createAreaPiloto(areaPayload);
      }

      navigate("/projetos");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao criar projeto");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/projetos")}
          className="w-8 h-8 rounded-lg border border-background-200 flex items-center justify-center text-foreground-500 hover:bg-background-50 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading">Novo Projeto</h1>
          <p className="text-xs text-foreground-500">Cadastro guiado em {STEPS.length} etapas</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors w-full cursor-pointer ${
                i === step
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : i < step
                    ? "bg-success-50 text-success-700 border border-success-200"
                    : "bg-background-50 text-foreground-400 border border-background-200"
              }`}
            >
              <i className={`${i < step ? "ri-check-line" : s.icon} text-sm`}></i>
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={`w-4 h-0.5 flex-shrink-0 ${i < step ? "bg-success-300" : "bg-background-200"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-card border border-background-200/70 p-6">
        {step === 0 && (
          <StepProjeto
            form={projectForm}
            onChange={setProjectForm}
            clients={clients}
            loadingClients={loadingClients}
          />
        )}
        {step === 1 && (
          <StepMunicipio
            form={municipalityForm}
            onChange={setMunicipalityForm}
            municipalities={municipalities}
            loading={loadingMunicipalities}
          />
        )}
        {step === 2 && <StepAreaEstudo form={areaForm} onChange={setAreaForm} />}
        {step === 3 && (
          <StepResumo
            projectForm={projectForm}
            municipalityForm={municipalityForm}
            areaForm={areaForm}
            projectTypeLabel={projectTypeLabel()}
            clientName={clientName()}
            selectedMunicipality={selectedMunicipality()}
          />
        )}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-danger-50 border border-danger-200 text-sm text-danger-700">
            <i className="ri-error-warning-line mr-1"></i>
            {error}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => (step > 0 ? setStep(step - 1) : navigate("/projetos"))}
          className="btn-outline text-xs px-4 py-2 cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1"></i>
          {step === 0 ? "Cancelar" : "Voltar"}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canAdvance()}
            className="btn-primary text-xs px-4 py-2 cursor-pointer disabled:opacity-50"
          >
            Próximo
            <i className="ri-arrow-right-line ml-1"></i>
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary text-xs px-5 py-2 cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1"></i>Criando...
              </>
            ) : (
              <>
                <i className="ri-check-line mr-1"></i>Criar Projeto
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// --- Step Components ---

function StepProjeto({
  form,
  onChange,
  clients,
  loadingClients,
}: {
  form: ProjectForm;
  onChange: (f: ProjectForm) => void;
  clients: { id: number; name: string }[];
  loadingClients: boolean;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
        <i className="ri-folder-chart-line text-primary-600"></i>
        Dados do Projeto
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-foreground-700 mb-1">
            Nome do Projeto <span className="text-danger-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            placeholder="Ex: CTA Goiânia - Setor Bela Vista"
            className="input-field"
            maxLength={220}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-foreground-700 mb-1">
            Tipo de Projeto <span className="text-danger-500">*</span>
          </label>
          <select
            value={form.project_type}
            onChange={(e) => onChange({ ...form, project_type: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione o tipo...</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-foreground-700 mb-1">Descrição</label>
          <textarea
            value={form.description}
            onChange={(e) => onChange({ ...form, description: e.target.value })}
            placeholder="Descreva brevemente o escopo do projeto..."
            rows={3}
            className="input-field resize-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1">Cliente</label>
          <select
            value={form.client_id ?? ""}
            onChange={(e) => onChange({ ...form, client_id: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="input-field"
            disabled={loadingClients}
          >
            <option value="">Nenhum</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div></div>
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1">Data de Início</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => onChange({ ...form, start_date: e.target.value })}
            className="input-field"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1">Previsão de Término</label>
          <input
            type="date"
            value={form.expected_end_date}
            onChange={(e) => onChange({ ...form, expected_end_date: e.target.value })}
            className="input-field"
          />
        </div>
      </div>
    </div>
  );
}

function StepMunicipio({
  form,
  onChange,
  municipalities,
  loading,
}: {
  form: MunicipalityForm;
  onChange: (f: MunicipalityForm) => void;
  municipalities: MunicipalityApi[];
  loading: boolean;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
        <i className="ri-map-pin-line text-primary-600"></i>
        Município
      </h2>

      <div className="flex gap-2">
        <button
          onClick={() => onChange({ ...form, mode: "existing" })}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
            form.mode === "existing"
              ? "bg-primary-50 text-primary-700 border-primary-200"
              : "text-foreground-500 border-background-200 hover:bg-background-50"
          }`}
        >
          <i className="ri-list-check mr-1"></i>Existente
        </button>
        <button
          onClick={() => onChange({ ...form, mode: "new" })}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
            form.mode === "new"
              ? "bg-primary-50 text-primary-700 border-primary-200"
              : "text-foreground-500 border-background-200 hover:bg-background-50"
          }`}
        >
          <i className="ri-add-line mr-1"></i>Novo
        </button>
      </div>

      {form.mode === "existing" ? (
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1">
            Município <span className="text-danger-500">*</span>
          </label>
          <select
            value={form.existing_id ?? ""}
            onChange={(e) => onChange({ ...form, existing_id: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="input-field"
            disabled={loading}
          >
            <option value="">{loading ? "Carregando..." : "Selecione..."}</option>
            {municipalities.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} — {m.uf}{m.ibge_code ? ` (IBGE ${m.ibge_code})` : ""}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-700 mb-1">
              Nome <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => onChange({ ...form, name: e.target.value })}
              placeholder="Ex: Goiânia"
              className="input-field"
              maxLength={180}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">
              UF <span className="text-danger-500">*</span>
            </label>
            <select
              value={form.uf}
              onChange={(e) => onChange({ ...form, uf: e.target.value })}
              className="input-field"
            >
              {UFS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Código IBGE</label>
            <input
              type="text"
              value={form.ibge_code}
              onChange={(e) => onChange({ ...form, ibge_code: e.target.value })}
              placeholder="Ex: 5208707"
              className="input-field"
              maxLength={20}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function StepAreaEstudo({
  form,
  onChange,
}: {
  form: AreaEstudoForm;
  onChange: (f: AreaEstudoForm) => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
        <i className="ri-map-2-line text-primary-600"></i>
        Área de Estudo
      </h2>

      <label className="flex items-center gap-2 text-xs text-foreground-700 cursor-pointer">
        <input
          type="checkbox"
          checked={form.skip}
          onChange={(e) => onChange({ ...form, skip: e.target.checked })}
          className="rounded border-background-300"
        />
        Pular — criar área de estudo depois
      </label>

      {!form.skip && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">
              Nome <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => onChange({ ...form, name: e.target.value })}
              placeholder="Ex: Setor Bela Vista"
              className="input-field"
              maxLength={220}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">
              Código <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => onChange({ ...form, code: e.target.value })}
              placeholder="Ex: SBV-001"
              className="input-field"
              maxLength={80}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Bairro</label>
            <input
              type="text"
              value={form.neighborhood}
              onChange={(e) => onChange({ ...form, neighborhood: e.target.value })}
              placeholder="Ex: Setor Bela Vista"
              className="input-field"
              maxLength={160}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Quadras Esperadas</label>
            <input
              type="number"
              value={form.expected_block_count}
              onChange={(e) => onChange({ ...form, expected_block_count: e.target.value })}
              placeholder="Ex: 39"
              className="input-field"
              min={0}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-700 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={(e) => onChange({ ...form, description: e.target.value })}
              placeholder="Informações adicionais sobre a área..."
              rows={2}
              className="input-field resize-none"
            />
          </div>
        </div>
      )}

      <p className="text-xs text-foreground-400 flex items-start gap-1">
        <i className="ri-information-line mt-0.5"></i>
        A geometria (limite da área) pode ser adicionada depois pelo Console de Edição Territorial.
      </p>
    </div>
  );
}

function StepResumo({
  projectForm,
  municipalityForm,
  areaForm,
  projectTypeLabel,
  clientName,
  selectedMunicipality,
}: {
  projectForm: ProjectForm;
  municipalityForm: MunicipalityForm;
  areaForm: AreaEstudoForm;
  projectTypeLabel: string;
  clientName: string;
  selectedMunicipality: MunicipalityApi | undefined;
}) {
  return (
    <div className="space-y-5">
      <h2 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
        <i className="ri-check-double-line text-primary-600"></i>
        Confirme os dados
      </h2>

      <SummarySection title="Projeto" icon="ri-folder-chart-line">
        <SummaryRow label="Nome" value={projectForm.name} />
        <SummaryRow label="Tipo" value={projectTypeLabel} />
        {projectForm.description && <SummaryRow label="Descrição" value={projectForm.description} />}
        <SummaryRow label="Cliente" value={clientName} />
        {projectForm.start_date && <SummaryRow label="Início" value={projectForm.start_date} />}
        {projectForm.expected_end_date && <SummaryRow label="Previsão de Término" value={projectForm.expected_end_date} />}
      </SummarySection>

      <SummarySection title="Município" icon="ri-map-pin-line">
        {municipalityForm.mode === "existing" && selectedMunicipality ? (
          <>
            <SummaryRow label="Nome" value={selectedMunicipality.name} />
            <SummaryRow label="UF" value={selectedMunicipality.uf} />
            {selectedMunicipality.ibge_code && <SummaryRow label="IBGE" value={selectedMunicipality.ibge_code} />}
          </>
        ) : (
          <>
            <SummaryRow label="Nome" value={municipalityForm.name} />
            <SummaryRow label="UF" value={municipalityForm.uf} />
            {municipalityForm.ibge_code && <SummaryRow label="IBGE" value={municipalityForm.ibge_code} />}
            <p className="text-xs text-info-600 mt-1">
              <i className="ri-add-circle-line mr-1"></i>Será criado automaticamente
            </p>
          </>
        )}
      </SummarySection>

      <SummarySection title="Área de Estudo" icon="ri-map-2-line">
        {areaForm.skip ? (
          <p className="text-xs text-foreground-500 italic">Será criada posteriormente</p>
        ) : (
          <>
            <SummaryRow label="Nome" value={areaForm.name} />
            <SummaryRow label="Código" value={areaForm.code} />
            {areaForm.neighborhood && <SummaryRow label="Bairro" value={areaForm.neighborhood} />}
            {areaForm.expected_block_count && <SummaryRow label="Quadras" value={areaForm.expected_block_count} />}
          </>
        )}
      </SummarySection>
    </div>
  );
}

function SummarySection({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="p-4 rounded-lg border border-background-200 bg-background-50/50">
      <h3 className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
        <i className={`${icon} text-sm text-primary-500`}></i>
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="text-foreground-500 min-w-[100px]">{label}:</span>
      <span className="text-foreground-900 font-medium">{value}</span>
    </div>
  );
}
